'use client';

import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { ESMExitedStockItem } from '@/types/esmExitedStocks';
import StockActionIcons from '@/components/stocks/StockActionIcons';
import ESMStockDetailModal from '@/components/esm-stock/ESMStockDetailModal';

interface ESMExitedStocksTableProps {
  stocks: ESMExitedStockItem[];
}

type SortField = 'stock_name' | 'current_price' | 'market_capitalization' | 'price_to_earning' | 'transition_date';
type SortDirection = 'asc' | 'desc';

export default function ESMExitedStocksTable({ stocks }: ESMExitedStocksTableProps) {
  const [sortField, setSortField] = useState<SortField>('transition_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTransitionDate, setSelectedTransitionDate] = useState('');
  const [selectedStock, setSelectedStock] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(dir);
  };

  const handleStockClick = (isin: string) => {
    setSelectedStock(isin);
  };

  const handleCloseModal = () => {
    setSelectedStock(null);
  };

  // Get unique transition dates for filtering
  const transitionDates = useMemo(() => {
    const dates = stocks
      .map(s => s.transition_date)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return dates;
  }, [stocks]);

  // Filter and sort stocks
  const filteredAndSortedStocks = useMemo(() => {
    // Start with all stocks
    let filtered = stocks;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(stock => 
        (stock.stock_name && stock.stock_name.toLowerCase().includes(query)) ||
        (stock.isin && stock.isin.toLowerCase().includes(query)) ||
        (stock.nse_code && stock.nse_code.toLowerCase().includes(query)) ||
        (stock.bse_code && stock.bse_code.toLowerCase().includes(query)) ||
        (stock.stock_code && stock.stock_code.toLowerCase().includes(query))
      );
    }

    // Apply transition date filter
    if (selectedTransitionDate) {
      filtered = filtered.filter(stock => stock.transition_date === selectedTransitionDate);
    }

    // Apply sorting
    const copy = [...filtered];
    return copy.sort((a, b) => {
      let av: unknown, bv: unknown;
      
      if (sortField === 'transition_date') {
        av = a.transition_date;
        bv = b.transition_date;
      } else {
        av = a[sortField as keyof ESMExitedStockItem];
        bv = b[sortField as keyof ESMExitedStockItem];
      }
      
      const m = sortDirection === 'asc' ? 1 : -1;
      
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * m;
      if (bv == null) return -1 * m;
      
      if (sortField === 'transition_date') {
        const ad = new Date(av as string).getTime();
        const bd = new Date(bv as string).getTime();
        if (isNaN(ad) && isNaN(bd)) return 0;
        if (isNaN(ad)) return 1 * m;
        if (isNaN(bd)) return -1 * m;
        return (ad - bd) * m;
      }
      
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * m;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * m;
      return av < bv ? -1 * m : av > bv ? 1 * m : 0;
    });
  }, [stocks, sortField, sortDirection, searchQuery, selectedTransitionDate]);

  const formatMarketCap = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 1000) return `${sign}₹${(absValue / 1000).toFixed(2)}K Cr`;
    if (absValue >= 1) return `${sign}₹${absValue.toFixed(2)} Cr`;
    return `${sign}₹${(absValue * 100).toFixed(2)} L`;
  };

  const formatRatio = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return value.toFixed(2);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const exportToCSV = () => {
    const headers = [
      'ISIN', 'Stock Name', 'NSE Code', 'BSE Code', 'Stock Code',
      'Current Price', 'Market Cap (Cr)', 'P/E Ratio', 'Current ESM Stage',
      'Transaction Type', 'Transition Date', 'TradingView Link', 'Screener Link', 'External Code'
    ];
    
    const csvData = filteredAndSortedStocks.map(stock => [
      stock.isin,
      `"${stock.stock_name?.replace(/"/g, '""')}"`,
      stock.nse_code || '',
      stock.bse_code || '',
      stock.stock_code || '',
      stock.current_price || 'N/A',
      stock.market_capitalization || 'N/A',
      stock.price_to_earning || 'N/A',
      stock.current_esm_stage,
      stock.transaction_type,
      stock.transition_date,
      stock.trading_view_navlink || 'N/A',
      stock.screener_navlink || 'N/A',
      stock.stock_code ? `"${stock.stock_code},"` : ''
    ]);

    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `esm_exited_stocks_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="w-4 h-4 text-zinc-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> : 
      <ChevronDownIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
  };

  if (!stocks.length) {
    return (
      <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
        <p className="text-zinc-500 dark:text-zinc-400 text-lg">No ESM exited stocks found</p>
        <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden table-container-with-popups">
      {/* Header with Search and Export Button */}
      <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                ESM Exited Stocks ({filteredAndSortedStocks.length}{filteredAndSortedStocks.length !== stocks.length ? ` of ${stocks.length}` : ''})
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Stocks that have exited from ESM framework with historical data
              </p>
            </div>
            
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col gap-4">
            {/* First Row of Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Transition Date Filter */}
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Transition Date
                </label>
                <select
                  value={selectedTransitionDate}
                  onChange={(e) => setSelectedTransitionDate(e.target.value)}
                  className="block w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value="">All Dates</option>
                  {transitionDates.map(date => (
                    <option key={date} value={date}>
                      {formatDate(date)} ({stocks.filter(s => s.transition_date === date).length})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Second Row - Search */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1 max-w-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search stocks, ISIN, codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <XMarkIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper-popup-safe min-table-height">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800/60">
              <tr>
                <th 
                  onClick={() => handleSort('stock_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    Stock Details
                    <SortIcon field="stock_name" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('market_capitalization')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    Market Cap
                    <SortIcon field="market_capitalization" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('price_to_earning')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    P/E Ratio
                    <SortIcon field="price_to_earning" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('transition_date')}
                  className="px-4 py-3 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1">
                    Exit Details
                    <SortIcon field="transition_date" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
              {filteredAndSortedStocks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <MagnifyingGlassIcon className="h-12 w-12 text-zinc-400 mb-4" />
                      <p className="text-zinc-500 dark:text-zinc-400 text-lg">No stocks match your search</p>
                      <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">
                        {searchQuery ? 'Try adjusting your search terms' : 'No stocks available'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedStocks.map((stock) => (
                  <tr 
                    key={stock.isin} 
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors table-row-with-menu"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex-1">
                        <StockActionIcons
                          isin={stock.isin}
                          stockName={stock.stock_name}
                          tradingViewLink={stock.trading_view_navlink}
                          screenerLink={stock.screener_navlink}
                          currentPrice={stock.current_price || undefined}
                          showNotesCount
                        />
                        <div 
                          className="text-sm font-medium text-zinc-900 dark:text-white cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400"
                          onClick={() => handleStockClick(stock.isin)}
                        >
                          {stock.stock_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                      {formatMarketCap(stock.market_capitalization)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                      {formatRatio(stock.price_to_earning)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="space-y-2">
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="h-4 w-4 text-red-500" />
                            <span className="text-sm font-medium text-zinc-900 dark:text-white">
                              {formatDate(stock.transition_date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-zinc-50 dark:bg-zinc-800/60 border-t border-zinc-200 dark:border-zinc-800">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Showing {filteredAndSortedStocks.length} stock{filteredAndSortedStocks.length !== 1 ? 's' : ''}
          {selectedTransitionDate && ` for transition date ${formatDate(selectedTransitionDate)}`}
        </p>
      </div>

      {/* ESM Stock Detail Modal */}
      {selectedStock && (
        <ESMStockDetailModal 
          isin={selectedStock} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
